import type { useClerk } from '@clerk/nextjs';
import type { TRPCClientError, TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type { AppRouter } from '~/server/routers/_app';

export const MISSING_CLERK_SESSION_MESSAGE = 'Error getting Clerk userId from NextJS request.';

const RELOAD_STORAGE_KEY = 'clerkSessionRecoveryReloadedAt';
const MIN_MS_BETWEEN_RELOADS = 5 * 60_000;

type RecoveryOutcome = 'fail' | 'leaving' | 'retry';

export type SessionRecoveryClerk = Pick<ReturnType<typeof useClerk>, 'redirectToSignIn'> & {
	session: { getToken: (options: { skipCache: boolean }) => Promise<string | null> } | null | undefined;
};

export type SessionRecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>;

export type ClerkSessionRecovery = {
	escalate: () => RecoveryOutcome;
	recover: () => Promise<RecoveryOutcome>;
};

export function createClerkSessionRecovery({
	currentUrl,
	getClerk,
	now,
	reloadPage,
	storage,
}: {
	currentUrl: () => string;
	getClerk: () => SessionRecoveryClerk | null;
	now: () => number;
	reloadPage: () => void;
	storage: () => SessionRecoveryStorage | null;
}): ClerkSessionRecovery {
	let leaving = false;
	let refreshing: Promise<string | null> | null = null;

	function lastReloadAt() {
		try {
			return Number(storage()?.getItem(RELOAD_STORAGE_KEY) ?? 0);
		} catch {
			return 0;
		}
	}

	function rememberReload() {
		try {
			storage()?.setItem(RELOAD_STORAGE_KEY, String(now()));
		} catch {
			return;
		}
	}

	function refreshToken(session: NonNullable<SessionRecoveryClerk['session']>) {
		refreshing ??= session
			.getToken({ skipCache: true })
			.catch(() => null)
			.finally(() => {
				refreshing = null;
			});

		return refreshing;
	}

	function escalate(): RecoveryOutcome {
		if (leaving) return 'leaving';

		const clerk = getClerk();
		if (!clerk) return 'fail';

		if (!clerk.session) {
			leaving = true;
			void clerk.redirectToSignIn({ redirectUrl: currentUrl() });
			return 'leaving';
		}

		if (now() - lastReloadAt() < MIN_MS_BETWEEN_RELOADS) return 'fail';

		leaving = true;
		rememberReload();
		reloadPage();
		return 'leaving';
	}

	async function recover(): Promise<RecoveryOutcome> {
		if (leaving) return 'leaving';

		const clerk = getClerk();
		if (!clerk?.session) return escalate();

		const token = await refreshToken(clerk.session);

		return token ? 'retry' : escalate();
	}

	return { escalate, recover };
}

function isMissingClerkSessionError(error: TRPCClientError<AppRouter>) {
	return error.data?.code === 'UNAUTHORIZED' && error.message === MISSING_CLERK_SESSION_MESSAGE;
}

export function clerkSessionRecoveryLink(recovery: ClerkSessionRecovery): TRPCLink<AppRouter> {
	return () =>
		({ next, op }) =>
			observable((observer) => {
				function attempt(isRetry: boolean) {
					return next(op).subscribe({
						complete: () => observer.complete(),
						error: (error) => {
							if (!isMissingClerkSessionError(error)) {
								observer.error(error);
								return;
							}

							void (isRetry ? Promise.resolve(recovery.escalate()) : recovery.recover()).then((outcome) => {
								if (outcome === 'retry') subscription = attempt(true);
								if (outcome === 'fail') observer.error(error);
							});
						},
						next: (value) => observer.next(value),
					});
				}

				let subscription = attempt(false);

				return () => subscription.unsubscribe();
			});
}

let registeredClerk: SessionRecoveryClerk | null = null;

export function registerClerkForSessionRecovery(clerk: SessionRecoveryClerk | null) {
	registeredClerk = clerk;
}

export const browserClerkSessionRecovery = createClerkSessionRecovery({
	currentUrl: () => window.location.href,
	getClerk: () => registeredClerk,
	now: () => Date.now(),
	reloadPage: () => window.location.reload(),
	storage: () => (typeof window === 'undefined' ? null : window.sessionStorage),
});
